import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgStyle } from '@angular/common';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ThemePipe } from 'src/app/core/pipes/theme.pipe';
import { IconComponent } from 'src/app/ui/icon/icon/icon.component';
import { ModePillComponent } from 'src/app/ui/pills/mode-pill/mode-pill.component';
import { CurrencyAmountComponent } from 'src/app/ui/currency/currency-amount/currency-amount.component';
import { TranslocoDirective } from '@jsverse/transloco';
import type { SoloAmountDisplay } from '../../utils/solo-tiers.utils';

// A Solo bar stops short of full until the score reaches the target, so a full bar always means a
// win.
const UNBEATEN_PROGRESS_CAP = 0.97;

// The Solo header's pills, "Win $7" and "Outscore 1,000", over a thin bar filling with the score.
// Both pills and the bar turn lime together once the score reaches the target, which is why the
// Win pill is drawn here rather than by game-score.
@Component({
  selector: 'app-solo-target-progress',
  templateUrl: './solo-target-progress.component.html',
  styleUrls: ['./solo-target-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    ThemePipe,
    IconComponent,
    ModePillComponent,
    CurrencyAmountComponent,
    TranslocoDirective,
  ],
})
export class SoloTargetProgressComponent {
  readonly target = input.required<number>();
  readonly score = input.required<number>();
  // The attempt's prize; until it is known the Win pill reads "Win" alone.
  readonly prize = input<SoloAmountDisplay>();

  private readonly themeService = inject(ThemeService);

  // Wager settles a score equal to the target as a win.
  readonly beaten = computed(() => this.score() >= this.target());

  readonly progress = computed(() => {
    if (this.beaten()) {
      return 1;
    }
    const target = this.target();
    const ratio = target > 0 ? this.score() / target : 0;
    return Math.min(UNBEATEN_PROGRESS_CAP, Math.max(0, ratio));
  });

  readonly formattedTarget = computed(() => this.themeService.formatNumber(this.target()));

  readonly winPillState = computed(() => (this.beaten() ? 'beaten' : 'active'));

  private readonly state = computed(() =>
    this.beaten() ? 'pill-solo-target-beaten' : 'pill-solo-target',
  );

  readonly pillStyles = computed<[string, string][]>(() => [
    ['background', this.state()],
    ['border-radius', 'pill-solo-target'],
    ['type', 'pill-solo-target-label'],
    ['text-color', `${this.state()}-label`],
  ]);

  readonly iconFill = computed(() => `--${this.state()}-icon-fill`);

  readonly trackStyles: [string, string][] = [['background', 'bar-solo-target']];

  readonly fillStyles = computed<[string, string][]>(() => [
    ['background', this.beaten() ? 'bar-solo-target-progress-beaten' : 'bar-solo-target-progress'],
  ]);
}
